import { useEffect, useRef, useState } from "react";
import { useInView, useReducedMotion, type Variants } from "framer-motion";

/**
 * Shared entrance-animation variants for the dashboard's stagger-reveal.
 * Consumers pass `initial="hidden" animate="visible"` on the container and
 * spread `itemVariants` on each child via `variants={itemVariants}`.
 */
export const containerVariants: Variants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.02,
    },
  },
};

export const itemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] },
  },
};

/**
 * Gates rendering of a chart until its container scrolls into view (or
 * immediately, if the user prefers reduced motion) so recharts' own mount
 * animation doubles as a scroll-triggered "draw-in" effect.
 */
export function useChartReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  const prefersReducedMotion = useReducedMotion();
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (isInView || prefersReducedMotion) setRevealed(true);
  }, [isInView, prefersReducedMotion]);

  return { ref, revealed, isAnimationActive: !prefersReducedMotion };
}
